import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate as useRouterNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BadgeEuro, CheckCircle2, Clock, Loader2, MessageSquare, Phone, Plus, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import supportTicketService, {
  type SupportTicketConfig,
  type SupportTicketDetail,
  type SupportTicketSummary,
} from '@/services/supportTicketService';
import { SupportTicketForm } from './SupportTicketForm';
import { SupportTicketView } from './SupportTicketView';
import { SupportTicketStatusBadge } from './SupportTicketStatusBadge';
import { formatDateTime, formatEur } from './format';
import { hasUnseenTeamReply } from './seen';
import { useAssistantEnabled } from '@/hooks/useAssistantEnabled';
import { reportProblemPath } from '@/lib/assistantLinks';

function hasNewTeamReply(ticket: SupportTicketSummary): boolean {
  return hasUnseenTeamReply(ticket.id, ticket.last_message_author, ticket.last_message_at);
}

function PriceFacts({ config }: { config: SupportTicketConfig }) {
  const facts = [
    { icon: BadgeEuro, title: `${formatEur(config.price_eur)} netto pauschal`, text: 'pro Ticket' },
    { icon: Clock, title: `Bis zu ${config.included_hours} Stunden`, text: 'Aufwand inklusive' },
    { icon: CheckCircle2, title: 'Erst bei Lösung', text: 'wird berechnet' },
    { icon: ShieldCheck, title: 'Fehler bei uns?', text: 'Dann kostenlos' },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {facts.map(({ icon: Icon, title, text }) => (
        <div key={title} className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
          <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#1967D2]" />
          <div>
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <p className="text-xs text-muted-foreground">{text}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Hilfe › Support-Tickets. Navigation über die URL, damit Links funktionieren:
 * ?tab=tickets&new=1[&c=<Verlauf>] aus „Problem melden" im Verlauf bzw. vom
 * Assistenten, der erst analysiert und dann hierher führt,
 * ?tab=tickets&ticket=<id> aus unseren E-Mails.
 */
export function SupportTicketsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const routerNavigate = useRouterNavigate();
  const { enabled: assistantEnabled } = useAssistantEnabled();
  const [config, setConfig] = useState<SupportTicketConfig | null>(null);
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const ticketParam = Number(searchParams.get('ticket'));
  const openTicketId = Number.isInteger(ticketParam) && ticketParam > 0 ? ticketParam : null;
  const creating = searchParams.get('new') === '1';
  const convParam = Number(searchParams.get('c'));
  const initialConversationId = Number.isInteger(convParam) && convParam > 0 ? convParam : null;

  const navigate = useCallback(
    (view: { ticket?: number; create?: boolean; conversation?: number | null }) => {
      const next = new URLSearchParams();
      next.set('tab', 'tickets');
      if (view.ticket) next.set('ticket', String(view.ticket));
      if (view.create) next.set('new', '1');
      if (view.create && view.conversation) next.set('c', String(view.conversation));
      setSearchParams(next);
    },
    [setSearchParams],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, list] = await Promise.all([supportTicketService.getConfig(), supportTicketService.list()]);
      setConfig(cfg);
      setTickets(list);
    } catch (e: any) {
      toast.error(e?.message || 'Support-Tickets konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const upsert = useCallback((ticket: SupportTicketDetail) => {
    setTickets((prev) => {
      const rest = prev.filter((t) => t.id !== ticket.id);
      return [ticket, ...rest].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    });
  }, []);

  if (loading && !config) {
    return (
      <div className="flex justify-center py-16" role="status">
        <Loader2 className="h-6 w-6 animate-spin text-[#1967D2]" aria-hidden="true" />
        <span className="sr-only">Support-Tickets werden geladen…</span>
      </div>
    );
  }

  if (!config) {
    return (
      <Card className="shadow-sm border-border">
        <CardContent className="flex flex-col items-center gap-3 py-10 text-sm text-muted-foreground">
          Support-Tickets sind gerade nicht erreichbar.
          <Button variant="outline" size="sm" onClick={() => void load()}>
            Erneut versuchen
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (openTicketId) {
    return (
      <SupportTicketView
        key={openTicketId}
        ticketId={openTicketId}
        config={config}
        onBack={() => navigate({})}
        onChanged={upsert}
      />
    );
  }

  if (creating) {
    return (
      <SupportTicketForm
        config={config}
        initialConversationId={initialConversationId}
        onCancel={() => navigate({})}
        onCreated={(ticket) => {
          upsert(ticket);
          navigate({ ticket: ticket.id });
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-sm border-border">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-lg">Support-Tickets</CardTitle>
            <CardDescription className="max-w-2xl">
              Dein KI-Agent macht etwas nicht so, wie du es möchtest? Beschreib uns das Problem – wir sehen uns das
              Gespräch an, passen Einstellungen und freie Anweisungen an und melden uns bei dir.
            </CardDescription>
          </div>
          {/* Where the assistant is on, it looks at the problem first and leads to the form itself. */}
          <Button
            onClick={() => (assistantEnabled ? routerNavigate(reportProblemPath()) : navigate({ create: true }))}
            className="shrink-0 bg-[#1967D2] hover:bg-[#1558b0]"
          >
            <Plus className="mr-2 h-4 w-4" /> Neues Ticket
          </Button>
        </CardHeader>
        <CardContent>
          <PriceFacts config={config} />
        </CardContent>
      </Card>

      <Card className="shadow-sm border-border">
        <CardHeader>
          <CardTitle className="text-base">Deine Tickets</CardTitle>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Noch keine Tickets. Über „Neues Ticket" oder direkt aus einem Gesprächsverlauf („Problem melden").
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {tickets.map((t) => {
                const ChannelIcon = t.channel === 'voice' ? Phone : MessageSquare;
                const fresh = hasNewTeamReply(t);
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => navigate({ ticket: t.id })}
                      className="flex w-full flex-wrap items-center justify-between gap-3 rounded-md px-2 py-3 text-left transition-colors hover:bg-muted/60"
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <ChannelIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-foreground">
                            <span className="font-mono text-muted-foreground">#{t.id}</span> {t.category_label}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            eingereicht {formatDateTime(t.created_at)}
                          </span>
                        </span>
                      </span>
                      <span className="flex items-center gap-2">
                        {fresh && (
                          <span className="rounded-full bg-[#1967D2] px-2 py-0.5 text-[11px] font-semibold text-white">
                            Neue Antwort
                          </span>
                        )}
                        <SupportTicketStatusBadge status={t.status} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SupportTicketsPanel;
